import React, { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { httpsCallable } from "firebase/functions";
import { functions as firebaseFunctions } from "../../../firebase";
import useAuthStore from "../../../stores/authStore";
import "./EmailScheduler.css";

const listSchedules = httpsCallable(firebaseFunctions, "emailScheduler-listSchedules");
const createSchedule = httpsCallable(firebaseFunctions, "emailScheduler-createSchedule");
const updateSchedule = httpsCallable(firebaseFunctions, "emailScheduler-updateSchedule");
const deleteSchedule = httpsCallable(firebaseFunctions, "emailScheduler-deleteSchedule");
const sendTestEmail = httpsCallable(firebaseFunctions, "emailScheduler-sendTestEmail");

const getDefaultStartAtLocal = () =>
  dayjs().add(1, "day").hour(9).minute(0).second(0).millisecond(0).format("YYYY-MM-DDTHH:mm");

const getEmptyForm = () => ({
  toEmail: "",
  bccEmail: "",
  fromEmail: "",
  fromName: "Latest Sightings",
  subject: "",
  htmlBody: "",
  startAtLocal: getDefaultStartAtLocal(),
  timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
  scheduleType: "recurring",
  repeatInterval: "1",
  repeatUnit: "month",
});

const buildLatestSightingsSignatureHtml = () => `
  <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="width:100%;max-width:700px;font-family:Arial,sans-serif;color:#111111;">
    <tr>
      <td style="padding-top:24px;border-top:1px solid #dddddd;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0">
          <tr>
            <td style="vertical-align:top;padding-right:20px;">
              <img
                src="https://YOUR-PUBLIC-IMAGE-URL/logo.png"
                alt="Latest Sightings"
                width="120"
                style="display:block;width:120px;height:auto;border:0;"
              />
            </td>
            <td style="vertical-align:top;">
              <div style="font-size:20px;line-height:28px;font-weight:bold;">Latest Sightings Team</div>
              <div style="font-size:16px;line-height:24px;padding-top:10px;">+27 10 900 4285</div>
              <div style="font-size:16px;line-height:24px;padding-top:8px;">
                <a href="https://www.instagram.com/YOUR_ACCOUNT" style="color:#1a73e8;text-decoration:underline;">Instagram</a>
                <span style="color:#666666;"> | </span>
                <a href="https://www.tiktok.com/@YOUR_ACCOUNT" style="color:#1a73e8;text-decoration:underline;">TikTok</a>
                <span style="color:#666666;"> | </span>
                <a href="https://www.facebook.com/YOUR_PAGE" style="color:#1a73e8;text-decoration:underline;">Facebook</a>
                <span style="color:#666666;"> | </span>
                <a href="https://twitter.com/YOUR_ACCOUNT" style="color:#1a73e8;text-decoration:underline;">Twitter</a>
              </div>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
`;

const getPlainTextFromHtml = (html) =>
  String(html || "")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .trim();

const RichEmailEditor = ({ value, onChange }) => {
  const editorRef = useRef(null);
  const selectionRef = useRef(null);
  const [isHtmlMode, setIsHtmlMode] = useState(false);

  useEffect(() => {
    if (!isHtmlMode && editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || "";
    }
  }, [isHtmlMode, value]);

  const syncHtml = () => {
    onChange(editorRef.current?.innerHTML || "");
  };

  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      selectionRef.current = selection.getRangeAt(0);
    }
  };

  const restoreSelection = () => {
    const selection = window.getSelection();
    if (!selectionRef.current || !selection) {
      return;
    }

    selection.removeAllRanges();
    selection.addRange(selectionRef.current);
  };

  const withEditorSelection = (callback) => (event) => {
    event.preventDefault();
    editorRef.current?.focus();
    restoreSelection();
    callback();
    saveSelection();
    syncHtml();
  };

  const runCommand = (command, commandValue = null) => {
    document.execCommand(command, false, commandValue);
  };

  const handleAddLink = () => {
    const url = window.prompt("Enter the full link URL, including https://");
    if (!url) {
      return;
    }

    runCommand("createLink", url);
  };

  const handleAddImage = () => {
    const url = window.prompt("Enter the public image URL");
    if (!url) {
      return;
    }

    runCommand("insertImage", url);
  };

  const handleInsertSignature = () => {
    runCommand("insertHTML", buildLatestSightingsSignatureHtml());
  };

  const isEmpty = !getPlainTextFromHtml(value);

  return (
    <div className="rich-email-editor">
      <div className="rich-email-editor__toolbar">
        <button type="button" className="rich-email-editor__button" onMouseDown={withEditorSelection(() => runCommand("bold"))}>
          B
        </button>
        <button type="button" className="rich-email-editor__button" onMouseDown={withEditorSelection(() => runCommand("italic"))}>
          I
        </button>
        <button type="button" className="rich-email-editor__button" onMouseDown={withEditorSelection(() => runCommand("underline"))}>
          U
        </button>
        <button type="button" className="rich-email-editor__button" onMouseDown={withEditorSelection(() => runCommand("formatBlock", "h2"))}>
          H2
        </button>
        <button type="button" className="rich-email-editor__button" onMouseDown={withEditorSelection(() => runCommand("insertUnorderedList"))}>
          Bullets
        </button>
        <button type="button" className="rich-email-editor__button" onMouseDown={withEditorSelection(() => runCommand("insertOrderedList"))}>
          Numbers
        </button>
        <button type="button" className="rich-email-editor__button" onMouseDown={withEditorSelection(handleAddLink)}>
          Link
        </button>
        <button type="button" className="rich-email-editor__button" onMouseDown={withEditorSelection(handleAddImage)}>
          Image
        </button>
        <button type="button" className="rich-email-editor__button" onMouseDown={withEditorSelection(() => runCommand("insertHorizontalRule"))}>
          Line
        </button>
        <button type="button" className="rich-email-editor__button rich-email-editor__button--accent" onMouseDown={withEditorSelection(handleInsertSignature)}>
          Insert signature
        </button>
        <button
          type="button"
          className={`rich-email-editor__button ${isHtmlMode ? "rich-email-editor__button--active" : ""}`}
          onClick={() => setIsHtmlMode((currentValue) => !currentValue)}
        >
          {isHtmlMode ? "Visual" : "HTML"}
        </button>
      </div>

      {isHtmlMode ? (
        <textarea
          className="rich-email-editor__textarea"
          rows="14"
          placeholder="<p>Hello...</p>"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          required
        />
      ) : (
        <div
          ref={editorRef}
          className={`rich-email-editor__surface ${isEmpty ? "is-empty" : ""}`}
          contentEditable
          suppressContentEditableWarning
          data-placeholder="Write your email here, then use the toolbar to style it."
          onInput={syncHtml}
          onBlur={syncHtml}
          onKeyUp={saveSelection}
          onMouseUp={saveSelection}
          onFocus={saveSelection}
        />
      )}
    </div>
  );
};

const buildPreviewDocument = (htmlBody) => `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      body {
        margin: 0;
        background: #f4efe8;
        color: #2e2720;
        font-family: "IBM Plex Sans", "Segoe UI", sans-serif;
      }

      .preview-shell {
        min-height: 100vh;
        padding: 24px;
      }

      .preview-card {
        max-width: 760px;
        margin: 0 auto;
        padding: 24px;
        border-radius: 16px;
        background: #ffffff;
        box-shadow: 0 12px 30px rgba(26, 20, 15, 0.1);
      }

      .preview-empty {
        color: #7a6d62;
        font-size: 14px;
      }

      img {
        max-width: 100%;
        height: auto;
      }
    </style>
  </head>
  <body>
    <div class="preview-shell">
      <div class="preview-card">
        ${htmlBody || '<p class="preview-empty">Your email preview will show here once you paste the HTML body.</p>'}
      </div>
    </div>
  </body>
</html>`;

const describeCadence = (schedule) => {
  if (schedule.scheduleType === "once") {
    return "Send once";
  }

  const interval = Number(schedule.repeatInterval || 1);
  const unit = schedule.repeatUnit || "month";
  const label = interval === 1 ? unit : `${unit}s`;
  return `Every ${interval} ${label}`;
};

const formatDateTime = (isoString) => {
  if (!isoString) {
    return "Not scheduled";
  }

  return new Date(isoString).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

const EmailScheduler = () => {
  const [form, setForm] = useState(getEmptyForm);
  const [schedules, setSchedules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingScheduleId, setDeletingScheduleId] = useState("");
  const [testingScheduleId, setTestingScheduleId] = useState("");
  const [editingScheduleId, setEditingScheduleId] = useState("");
  const [feedback, setFeedback] = useState(null);
  const user = useAuthStore((state) => state.user);

  useEffect(() => {
    loadSchedules();
  }, []);

  const loadSchedules = async () => {
    setIsLoading(true);

    try {
      const response = await listSchedules();
      setSchedules(response.data?.schedules || []);
    } catch (error) {
      console.error("Unable to load email schedules:", error);
      setFeedback({
        type: "error",
        message: error.message || "Unable to load your saved email schedules yet.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setForm(getEmptyForm());
    setEditingScheduleId("");
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setForm((currentForm) => ({
      ...currentForm,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFeedback(null);

    if (!getPlainTextFromHtml(form.htmlBody)) {
      setFeedback({
        type: "error",
        message: "Please add some email content before saving the reminder.",
      });
      return;
    }

    setIsSaving(true);

    const payload = {
      ...form,
      repeatInterval: Number(form.repeatInterval || 1),
    };

    try {
      if (editingScheduleId) {
        await updateSchedule({
          scheduleId: editingScheduleId,
          ...payload,
        });
        setFeedback({
          type: "success",
          message: "Your reminder was updated and the next send date was recalculated.",
        });
      } else {
        await createSchedule(payload);
        setFeedback({
          type: "success",
          message: "Your reminder was saved. The server will send it when it becomes due.",
        });
      }

      resetForm();
      await loadSchedules();
    } catch (error) {
      console.error("Unable to save email schedule:", error);
      setFeedback({
        type: "error",
        message: error.message || "We could not save this reminder right now.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditSchedule = (schedule) => {
    setEditingScheduleId(schedule.id);
    setFeedback(null);
    setForm({
      toEmail: schedule.toEmail || "",
      bccEmail: schedule.bccEmail || "",
      fromEmail: schedule.fromEmail || "",
      fromName: schedule.fromName || "Latest Sightings",
      subject: schedule.subject || "",
      htmlBody: schedule.htmlBody || "",
      startAtLocal: schedule.startAtLocal || getDefaultStartAtLocal(),
      timeZone: schedule.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
      scheduleType: schedule.scheduleType || "recurring",
      repeatInterval: String(schedule.repeatInterval || 1),
      repeatUnit: schedule.repeatUnit || "month",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeleteSchedule = async (scheduleId) => {
    const shouldDelete = window.confirm(
      "Delete this scheduled reminder? This will stop any future sends for it."
    );

    if (!shouldDelete) {
      return;
    }

    setDeletingScheduleId(scheduleId);
    setFeedback(null);

    try {
      await deleteSchedule({ scheduleId });
      setFeedback({
        type: "success",
        message: "That reminder was deleted and will no longer send.",
      });

      if (editingScheduleId === scheduleId) {
        resetForm();
      }

      await loadSchedules();
    } catch (error) {
      console.error("Unable to delete email schedule:", error);
      setFeedback({
        type: "error",
        message: error.message || "We could not delete this reminder right now.",
      });
    } finally {
      setDeletingScheduleId("");
    }
  };

  const handleSendTestEmail = async (scheduleId) => {
    const requestedRecipient = window.prompt(
      "Send this test email to which address? Leave it as-is to use your own admin email.",
      user?.email || ""
    );

    if (requestedRecipient === null) {
      return;
    }

    const normalizedTestRecipient = requestedRecipient.trim();
    setTestingScheduleId(scheduleId);
    setFeedback(null);

    try {
      const response = await sendTestEmail({
        scheduleId,
        ...(normalizedTestRecipient ? { testRecipientEmail: normalizedTestRecipient } : {}),
      });
      setFeedback({
        type: "success",
        message: `Test email sent to ${response.data?.sentTo || user?.email || "your admin email"}.`,
      });
    } catch (error) {
      console.error("Unable to send test email:", error);
      setFeedback({
        type: "error",
        message: error.message || "We could not send the test email right now.",
      });
    } finally {
      setTestingScheduleId("");
    }
  };

  return (
    <div className="email-scheduler-page">
      <section className="email-scheduler-hero">
        <div>
          <p className="email-scheduler-eyebrow">Admin-only tool</p>
          <h1 className="email-scheduler-title">Email Scheduler</h1>
          <p className="email-scheduler-subtitle">
            Save recurring reminder emails here and the server will send them
            through Mailjet for you. Think of it like a calendar with an
            automatic send button.
          </p>
        </div>

        <div className="email-scheduler-hero-note">
          <strong>Good to know</strong>
          <span>
            Images in email signatures need public image links, not files from
            your computer. The send worker checks for due emails roughly every
            15 minutes.
          </span>
        </div>
      </section>

      {feedback && (
        <div className={`email-scheduler-banner ${feedback.type}`}>
          {feedback.message}
        </div>
      )}

      <div className="email-scheduler-layout">
        <form className="email-scheduler-card" onSubmit={handleSubmit}>
          <div className="email-scheduler-card-header">
            <div>
              <h2>{editingScheduleId ? "Edit reminder" : "New reminder"}</h2>
              <p>
                Fill in who should receive it, what it should say, and how often
                it should repeat.
              </p>
            </div>
          </div>

          <div className="email-scheduler-form-grid">
            <label className="email-scheduler-field">
              <span>To email address</span>
              <input
                name="toEmail"
                type="email"
                placeholder="client@example.com"
                value={form.toEmail}
                onChange={handleInputChange}
                required
              />
            </label>

            <label className="email-scheduler-field">
              <span>BCC email address</span>
              <input
                name="bccEmail"
                type="email"
                placeholder="copy@example.com"
                value={form.bccEmail}
                onChange={handleInputChange}
              />
            </label>

            <label className="email-scheduler-field">
              <span>From email address</span>
              <input
                name="fromEmail"
                type="email"
                placeholder="reminders@latestsightings.com"
                value={form.fromEmail}
                onChange={handleInputChange}
                required
              />
            </label>

            <label className="email-scheduler-field">
              <span>From name</span>
              <input
                name="fromName"
                type="text"
                placeholder="Latest Sightings"
                value={form.fromName}
                onChange={handleInputChange}
                required
              />
            </label>

            <label className="email-scheduler-field email-scheduler-field--wide">
              <span>Subject</span>
              <input
                name="subject"
                type="text"
                placeholder="Monthly reminder"
                value={form.subject}
                onChange={handleInputChange}
                required
              />
            </label>

            <label className="email-scheduler-field">
              <span>First send date and time</span>
              <input
                name="startAtLocal"
                type="datetime-local"
                value={form.startAtLocal}
                onChange={handleInputChange}
                required
              />
            </label>

            <label className="email-scheduler-field">
              <span>Time zone</span>
              <input
                name="timeZone"
                type="text"
                placeholder="Europe/London"
                value={form.timeZone}
                onChange={handleInputChange}
                required
              />
            </label>

            <label className="email-scheduler-field">
              <span>Schedule type</span>
              <select
                name="scheduleType"
                value={form.scheduleType}
                onChange={handleInputChange}
              >
                <option value="recurring">Repeat</option>
                <option value="once">Send once</option>
              </select>
            </label>

            <div className="email-scheduler-field">
              <span>Repeat every</span>
              <div className="email-scheduler-repeat-row">
                <input
                  name="repeatInterval"
                  type="number"
                  min="1"
                  max="999"
                  value={form.repeatInterval}
                  onChange={handleInputChange}
                  disabled={form.scheduleType === "once"}
                  required={form.scheduleType !== "once"}
                />
                <select
                  name="repeatUnit"
                  value={form.repeatUnit}
                  onChange={handleInputChange}
                  disabled={form.scheduleType === "once"}
                >
                  <option value="day">Day</option>
                  <option value="week">Week</option>
                  <option value="month">Month</option>
                  <option value="year">Year</option>
                </select>
              </div>
            </div>

            <label className="email-scheduler-field email-scheduler-field--full">
              <span>Email body</span>
              <RichEmailEditor
                value={form.htmlBody}
                onChange={(value) => setForm((currentForm) => ({
                  ...currentForm,
                  htmlBody: value,
                }))}
              />
              <small>
                Use the visual editor for normal emails. If you ever need full
                control, switch the editor to HTML mode.
              </small>
            </label>
          </div>

          <div className="email-scheduler-actions">
            <button className="action-button primary" type="submit" disabled={isSaving}>
              {isSaving
                ? "Saving..."
                : editingScheduleId
                  ? "Update reminder"
                  : "Save reminder"}
            </button>

            {editingScheduleId && (
              <button className="action-button" type="button" onClick={resetForm}>
                Cancel edit
              </button>
            )}
          </div>
        </form>

        <aside className="email-scheduler-card email-scheduler-preview-card">
          <div className="email-scheduler-card-header">
            <div>
              <h2>Preview</h2>
              <p>This is a safe preview inside the admin panel, so you can sanity-check the email before saving it.</p>
            </div>
          </div>

          <iframe
            className="email-scheduler-preview"
            title="Scheduled email preview"
            sandbox=""
            srcDoc={buildPreviewDocument(form.htmlBody)}
          />

          <div className="email-scheduler-tips">
            <div>
              <strong>Verified sender</strong>
              <span>Mailjet will only send from addresses you have already verified in your Mailjet account.</span>
            </div>
            <div>
              <strong>Images</strong>
              <span>Use normal web image URLs. Email apps cannot open images that only exist on your laptop.</span>
            </div>
            <div>
              <strong>BCC</strong>
              <span>
                Add one optional hidden-copy email if you want someone to quietly receive every send as well.
              </span>
            </div>
            <div>
              <strong>Test send</strong>
              <span>
                The test button sends a copy to {user?.email || "your logged-in admin email"} and does not change the real schedule.
              </span>
            </div>
            <div>
              <strong>Repeat timing</strong>
              <span>
                If you choose monthly, the reminder keeps using the same local
                clock time you set above.
              </span>
            </div>
          </div>
        </aside>
      </div>

      <section className="email-scheduler-card email-scheduler-list-card">
        <div className="email-scheduler-card-header">
          <div>
            <h2>Saved reminders</h2>
            <p>Each row below is one automatic email schedule that the backend will keep checking.</p>
          </div>

          <button className="action-button" type="button" onClick={loadSchedules}>
            Refresh
          </button>
        </div>

        {isLoading ? (
          <p className="loading-state">Loading scheduled reminders...</p>
        ) : schedules.length === 0 ? (
          <div className="email-scheduler-empty-state">
            <h3>No reminders saved yet</h3>
            <p>
              Once you save one, it will appear here with the next planned send
              time.
            </p>
          </div>
        ) : (
          <div className="table-shell">
            <table className="table">
              <thead>
                <tr>
                  <th>Recipient</th>
                  <th>Subject</th>
                  <th>Cadence</th>
                  <th>Next send</th>
                  <th>Last sent</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {schedules.map((schedule) => (
                  <tr key={schedule.id}>
                    <td>
                      <div className="email-scheduler-table-primary">{schedule.toEmail}</div>
                      <div className="email-scheduler-table-secondary">
                        From: {schedule.fromName} &lt;{schedule.fromEmail}&gt;
                      </div>
                      {schedule.bccEmail && (
                        <div className="email-scheduler-table-secondary">
                          BCC: {schedule.bccEmail}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="email-scheduler-table-primary">{schedule.subject}</div>
                      <div className="email-scheduler-table-secondary">
                        Starts: {schedule.startAtLocal?.replace("T", " ") || "Not set"} ({schedule.timeZone || "UTC"})
                      </div>
                    </td>
                    <td>{describeCadence(schedule)}</td>
                    <td>{formatDateTime(schedule.nextRunAt)}</td>
                    <td>{schedule.lastSentAt ? formatDateTime(schedule.lastSentAt) : "Not sent yet"}</td>
                    <td>
                      <span className={`email-scheduler-status email-scheduler-status--${schedule.status || "active"}`}>
                        {schedule.status || "active"}
                      </span>
                      {schedule.lastErrorMessage && (
                        <div className="email-scheduler-table-secondary email-scheduler-table-secondary--error">
                          Last error: {schedule.lastErrorMessage}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="email-scheduler-row-actions">
                        <button
                          className="action-button"
                          type="button"
                          disabled={testingScheduleId === schedule.id}
                          onClick={() => handleSendTestEmail(schedule.id)}
                        >
                          {testingScheduleId === schedule.id ? "Sending..." : "Test send"}
                        </button>
                        <button
                          className="action-button"
                          type="button"
                          onClick={() => handleEditSchedule(schedule)}
                        >
                          Edit
                        </button>
                        <button
                          className="action-button"
                          type="button"
                          disabled={deletingScheduleId === schedule.id}
                          onClick={() => handleDeleteSchedule(schedule.id)}
                        >
                          {deletingScheduleId === schedule.id ? "Deleting..." : "Delete"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default EmailScheduler;
